<template>
  <base-event-log-item
    icon="content_copy"
    :icon-color="iconColor"
    :researcher="referenceEventLog.eventBy"
    :date="referenceEventLog.createdAt"
    :bottom-node="bottomNode"
  >
    <div class="text-overflow-ellipsis">
      <span class="font-weight-light">{{ text }}</span>
      <span class="font-weight-medium">{{ actionText }}</span>
    </div>
  </base-event-log-item>
</template>

<script>
import BaseEventLogItem from './BaseEventLogItem';
import { ReferenceEventLog, Reference } from '@/models';
import { DuplicateState, ReferenceEvent } from '@/enums';

const DUPLICATE_STATE_TO_PROPS = {
  [DuplicateState.MAIN_COPY]: {
    text: (eventLog) =>
      `Found ${eventLog.payload.mainCopyOf.length} duplicate${
        eventLog.payload.mainCopyOf.length > 1 ? 's' : ''
      }`,
  },
  [DuplicateState.USER_DUPLICATE]: {
    text: 'Marked as a ',
    actionText: 'Duplicate',
  },
  [DuplicateState.AUTO_DUPLICATE]: {
    text: 'Automatically resolved as a ',
    actionText: 'Duplicate',
  },
  [DuplicateState.POTENTIAL_DUPLICATE]: {
    text: 'Flagged for ',
    actionText: 'Duplicate Review',
  },
  [DuplicateState.NOT_DUPLICATE]: {
    text: 'Marked as ',
    actionText: 'Not a Duplicate',
  },
};

export default {
  name: 'DuplicateEventLogItem',
  components: { BaseEventLogItem },
  props: {
    referenceEventLog: {
      type: ReferenceEventLog,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    bottomNode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    text() {
      return this.styleProps.text;
    },
    actionText() {
      return this.styleProps.actionText;
    },
    styleProps() {
      return Object.entries(
        DUPLICATE_STATE_TO_PROPS[this.duplicateState]
      ).reduce((acc, [key, value]) => {
        acc[key] =
          typeof value === 'function'
            ? value(this.referenceEventLog)
            : value;
        return acc;
      }, {});
    },
    duplicateState() {
      const duplicateStateFromEvent = this.referenceEventLog.payload.duplicateState();
      // Handle case where duplicates resolved before the event log was introduced
      // causes the event log to be in the AUTO_DUPLICATE state when it was
      // actually resolved by a user.
      if (duplicateStateFromEvent === DuplicateState.AUTO_DUPLICATE) {
        const markedAsDuplicateEvents = this.reference.eventLogs.filter(
          (eventLog) =>
            eventLog.event ===
              ReferenceEvent.DUPLICATE_STATE_UPDATE &&
            eventLog.payload.markedAsDuplicate
        );
        if (
          markedAsDuplicateEvents.length === 1 &&
          this.reference.isUserDuplicate()
        ) {
          return DuplicateState.USER_DUPLICATE;
        }
      }
      return duplicateStateFromEvent;
    },
    iconColor() {
      switch (this.duplicateState) {
        case DuplicateState.POTENTIAL_DUPLICATE:
          return 'warning';
        case DuplicateState.AUTO_DUPLICATE:
        case DuplicateState.USER_DUPLICATE:
          return 'error';
        default:
          return 'success';
      }
    },
  },
};
</script>

<style scoped>
.text-overflow-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
